/**
 * OVERVIEW — the first tab of the device page (Fleet Monitoring redesign,
 * `docs/mockups/fleet-monitoring-redesign-mockup.html` screen 2).
 *
 * Order, as the mockup asks it: what has this till REPORTED (open Issue
 * Console tickets, up to 5), then four cards — Software, Hardware, Health over
 * 24 h, Recent activity — each with a link to the tab that holds the rest.
 * Identity (photo, name, ids) and the at-a-glance facts live in the page
 * header above every tab, so this tab does not repeat them.
 *
 * It fetches only the 24 h health summary. The row, the command list and the
 * open issues come from the page — one read, shared, so the header badges and
 * these cards can never disagree about one till.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Unlink } from 'lucide-react';
import type { DeviceHealthSummary, FleetCommand } from '@platform/shared-types';
import { deviceHealthApi } from '../../../services/deviceHealthApi';
import { RubyLoader } from '../../../components/RubyLoader';
import { FLEET_UI_ACTIONS } from '../fleet/actionRegistry';
import { buildPeripheralLines } from './HealthTab';
import { IssueRow } from '../../fleet/device/IssuesTab';
import { TONE_PILL, isOpenIssue, updateStatus, type Tone } from '../../fleet/fleetStatus';
import { type DeviceTabId, type DeviceTabProps, formatWhen, relativeTime } from './deviceDetail';

const DASH = '—';
const CAP = 5;

const ACTION_LABEL: Record<string, string> = Object.fromEntries(FLEET_UI_ACTIONS.map((a) => [a.id, a.label]));
ACTION_LABEL.unlock = 'Unlock till';

function commandPill(c: FleetCommand): { label: string; tone: Tone } {
  switch (c.state) {
    case 'done': return { label: 'Done', tone: 'ok' };
    case 'queued': return { label: c.sentAt ? 'Queued' : 'Queued · offline', tone: 'warn' };
    case 'sent':
    case 'acked': return { label: 'Waiting for till', tone: 'info' };
    case 'failed': return { label: 'Failed', tone: 'bad' };
    case 'expired': return { label: 'Timed out', tone: 'bad' };
    default: return { label: 'Cancelled', tone: 'mute' };
  }
}

function Pill({ tone, children }: { tone: Tone; children: React.ReactNode }) {
  return (
    <span className={`inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-[11.5px] font-semibold ${TONE_PILL[tone]}`}>
      {children}
    </span>
  );
}

function LinkButton({ to, goTo, children }: { to: DeviceTabId; goTo?: (t: DeviceTabId) => void; children: React.ReactNode }) {
  if (!goTo) return null;
  return (
    <button
      type="button"
      onClick={() => goTo(to)}
      className="min-h-[44px] rounded-lg px-2.5 text-[13px] font-semibold text-[#DC143C] hover:bg-[#FFF1F3]"
    >
      {children}
    </button>
  );
}

function Card({ title, aside, children }: { title: string; aside?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="min-w-0 rounded-2xl border border-stone-200 bg-white">
      <div className="flex min-h-[52px] items-center justify-between gap-2 border-b border-stone-200 px-4 py-1.5">
        <h2 className="text-[14px] font-semibold text-stone-900">{title}</h2>
        {aside}
      </div>
      {children}
    </section>
  );
}

/**
 * `min-w-0` on the value is load-bearing: a build string like
 * `2026.09.24+610` is one unbreakable token, and without it the card's grid
 * track widens and pushes the neighbouring card off-screen.
 */
function KV({ rows }: { rows: [string, React.ReactNode][] }) {
  return (
    <dl className="m-0 grid grid-cols-1 gap-x-3 gap-y-2 px-4 py-3 text-[13px] sm:grid-cols-[150px_minmax(0,1fr)]">
      {rows.map(([k, v]) => (
        <div key={k} className="contents">
          <dt className="text-stone-500">{k}</dt>
          <dd className="m-0 min-w-0 break-words text-stone-900">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

const VERDICT: Record<string, { label: string; tone: Tone }> = {
  ok: { label: 'OK', tone: 'ok' },
  missing: { label: 'Missing', tone: 'bad' },
  unknown: { label: 'Unknown', tone: 'mute' },
};

export default function OverviewTab({ row, model, goTo, commands, openIssues }: DeviceTabProps) {
  const [summary, setSummary] = useState<DeviceHealthSummary | null | 'error'>(null);

  useEffect(() => {
    if (!row.deviceId) return;
    let live = true;
    setSummary(null);
    deviceHealthApi.summary({ scope: 'device', id: row.deviceId, window: '24h' })
      .then((s) => { if (live) setSummary(s); })
      .catch(() => { if (live) setSummary('error'); });
    return () => { live = false; };
  }, [row.deviceId]);

  const list = commands || [];
  const report = useMemo(() => list.find((c) => c.action === 'device_report' && c.state === 'done'), [list]);
  const lines = useMemo(() => buildPeripheralLines((report?.result as Record<string, unknown> | null) ?? null), [report]);
  const open = (openIssues || []).filter(isOpenIssue);
  const status = updateStatus(row);
  const modelName = (model as { modelName?: string } | null | undefined)?.modelName
    || [row.hardwareBrand, row.hardwareModel].filter(Boolean).join(' ') || DASH;

  return (
    <div className="grid gap-4">
      <Card title="Reported issues" aside={<LinkButton to="issues" goTo={goTo}>All issues for this till</LinkButton>}>
        {openIssues === undefined ? (
          <RubyLoader variant="section" label="Loading issues…" />
        ) : openIssues === null ? (
          <p role="alert" className="px-4 py-4 text-[13px] text-rose-700">Could not load this till's issues. The Issues tab retries.</p>
        ) : open.length === 0 ? (
          <p className="px-4 py-6 text-center text-[13px] text-stone-500">No open issues on this till.</p>
        ) : (
          <ul>{open.slice(0, CAP).map((p) => <IssueRow key={`${p.fingerprint}-${p.tenantId}`} problem={p} />)}</ul>
        )}
      </Card>

      {row.unitJoinSource === 'none' && (
        <p className="flex flex-wrap items-center gap-2 text-[12.5px] text-stone-500">
          <Unlink size={14} aria-hidden="true" />
          No hardware unit joined — photo, serial and install dates live on the unit record.
          <Link to="/pos-config?tab=install" className="inline-flex min-h-[44px] items-center font-semibold text-[#DC143C] hover:underline">
            Link it in Install →
          </Link>
        </p>
      )}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <Card title="Software" aside={<LinkButton to="updates" goTo={goTo}>Manage updates</LinkButton>}>
          <KV rows={[
            ['Running', <span key="r" className="font-mono">{row.appVersion || row.displayVersion || DASH}</span>],
            ['Should run', row.targetVersion
              ? <span key="t"><span className="font-mono">{row.targetVersion}</span> · {row.targetSource === 'pin' ? 'pinned' : `${row.releaseRing} ring`}</span>
              : <span key="t" className="text-amber-700">No release covers this till — promote a build in Releases → Apps</span>],
            ['When it installs', <span key="w"><Pill tone={status.tone}>{status.label}</Pill> <span className="text-stone-500">{status.detail}</span></span>],
            ['Last install', row.installedAt
              ? [formatWhen(row.installedAt), row.installedBy ? `by ${row.installedBy}` : null, row.lastKnownInstallBuild].filter(Boolean).join(' · ')
              : DASH],
            ['Version ledger', goTo
              ? <button key="l" type="button" onClick={() => goTo('history')} className="font-semibold text-[#DC143C] hover:underline">Every version change →</button>
              : DASH],
          ]} />
        </Card>

        <Card
          title="Hardware"
          aside={<Pill tone="mute">{report ? `as of device report · ${relativeTime(report.completedAt ?? report.createdAt) || DASH}` : 'no device report yet'}</Pill>}
        >
          <KV rows={[
            ['Model', modelName],
            ...lines.map((l): [string, React.ReactNode] => [
              l.name,
              <span key={l.key}><Pill tone={VERDICT[l.verdict].tone}>{VERDICT[l.verdict].label}</Pill>{l.sub ? <span className="ml-2 text-stone-500">{l.sub}</span> : null}</span>,
            ]),
          ]} />
        </Card>

        <Card title="Health · 24h" aside={<LinkButton to="health" goTo={goTo}>Open health</LinkButton>}>
          {!row.deviceId ? (
            <p className="px-4 py-4 text-[13px] text-stone-500">This row has never checked in.</p>
          ) : summary === null ? (
            <RubyLoader variant="section" label="Loading health…" />
          ) : summary === 'error' ? (
            <p className="px-4 py-4 text-[13px] text-stone-500">Health numbers are unavailable right now.</p>
          ) : (
            <KV rows={[
              ['Crash-free', summary.crashFreePct == null ? `${DASH} · ${summary.sessions} sessions` : `${Math.round(summary.crashFreePct * 10) / 10}% · ${summary.sessions} sessions`],
              ['Errors', String(summary.errors)],
              ['Crashes', String(summary.crashes)],
              ['Print failures', String(summary.printFailures)],
              ['Open issues', goTo
                ? <button key="i" type="button" onClick={() => goTo('issues')} className="font-semibold text-[#DC143C] hover:underline">{open.length} · see Issues tab</button>
                : String(open.length)],
            ]} />
          )}
        </Card>

        <Card title="Recent activity" aside={<LinkButton to="history" goTo={goTo}>All history</LinkButton>}>
          {list.length === 0 ? (
            <p className="px-4 py-6 text-center text-[13px] text-stone-500">Nothing sent to this till yet.</p>
          ) : (
            <ul className="px-4 py-1">
              {list.slice(0, CAP).map((c) => {
                const pill = commandPill(c);
                return (
                  <li key={c.commandId} data-testid="activity-row" className="flex items-start gap-2.5 border-b border-dashed border-stone-200 py-2.5 text-[13px] last:border-b-0">
                    <span className="min-w-0">{ACTION_LABEL[c.action] || c.action.replace(/_/g, ' ')} by {c.requestedBy}</span>
                    <Pill tone={pill.tone}>{pill.label}</Pill>
                    <span className="ml-auto whitespace-nowrap text-[12px] text-stone-500">{relativeTime(c.createdAt) || DASH}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}
